import type { GarmentMovementReport } from '@/api/reports';
import type { PrintLetterhead } from '@core/api/bookings';
import {
  countCell,
  dayLabel,
  movementColumns,
  movementWords,
  rangeWords,
} from '@core/reports/garmentMovement';

import { documentClass, type PaperSize } from './paper';

/**
 * Report A1 on paper — garments in and out: the shop, the range, a row per shop day with the seven
 * counts and the totals underneath. Web only (a report is not handed to anyone), so it prints
 * through `PrintFrame` as the goods receipt does.
 */
export function GarmentMovementSheet({
  paper,
  report,
  letterhead,
  branchName,
}: {
  paper: PaperSize;
  report: GarmentMovementReport;
  letterhead: PrintLetterhead | undefined;
  branchName: string;
}) {
  const days = report.days.length;

  return (
    <article id="printableCard" className={`${documentClass(paper)} px-7 py-6`}>
      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">
            {letterhead?.tenantName ?? branchName}
          </p>
          <p className="text-[0.85em]">{letterhead?.branchName ?? branchName}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Garments in and out</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            {rangeWords(report.from, report.to)}
          </p>
        </div>
      </header>

      <table className="mt-5 w-full border-collapse text-[0.9em]">
        <thead>
          <tr className="border-b border-[color:var(--doc-rule-strong)] text-right text-[0.85em] text-[color:var(--doc-muted)]">
            <th className="py-1.5 pr-2 text-left font-semibold">Day</th>
            {movementColumns.map((column) => (
              <th key={column.key} className="py-1.5 pl-2 font-semibold">
                {movementWords[column.kind]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.days.map((day) => (
            <tr
              key={day.day}
              className="border-b border-[color:var(--doc-rule)] text-right break-inside-avoid"
            >
              <td className="py-1 pr-2 text-left">{dayLabel(day.day)}</td>
              {movementColumns.map((column) => (
                <td key={column.key} className="py-1 pl-2 font-mono">
                  {countCell(day.counts[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[color:var(--doc-rule-strong)] text-right font-bold">
            <td className="py-1.5 pr-2 text-left">
              {days} day{days === 1 ? '' : 's'}
            </td>
            {movementColumns.map((column) => (
              <td key={column.key} className="py-1.5 pl-2 font-mono">
                {report.total[column.key]}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>

      <p className="mt-4 text-[0.8em] text-[color:var(--doc-muted)]">
        Days are the shop’s own. Booked in counts new garments into stock — a supplier’s delivery or
        an import.
      </p>
    </article>
  );
}
